import React, { useState, useEffect, useRef } from 'react';
import { referencesAPI } from '../services/api';
import useDialog from '../hooks/useDialog';
import './EventModal.css';

const EventModal = ({ isOpen, onClose, onSave, onDelete, event, mode, defaultDate, objects = [], defaultObjectId, canEditObjects = true, canEditStatus = true, userRole, userOrganizationId }) => {
  const addObjectSelectRef = useRef(null);
  const prevEventRef = useRef(null);
  const objectsRef = useRef(objects);
  objectsRef.current = objects; // Keep objects ref in sync
  const [organizations, setOrganizations] = useState([]);
  const [sportTypes, setSportTypes] = useState([]);
  const [validationError, setValidationError] = useState('');
  const dialogRef = useDialog({ isOpen, onClose });
  const [formData, setFormData] = useState({
    name: '',
    startDate: '',
    endDate: '',
    description: '',
    cloudUrl: '',
    status: 'not_approved',
    objectIds: [],
    objectDates: {},
    organizationId: '',
    sportTypeId: ''
  });

  // Формат для datetime-local в локальном времени (не UTC)
  const toDatetimeLocal = (dateStr) => {
    if (!dateStr) return '';
    const d = new Date(dateStr);
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    const h = String(d.getHours()).padStart(2, '0');
    const min = String(d.getMinutes()).padStart(2, '0');
    return `${y}-${m}-${day}T${h}:${min}`;
  };

  useEffect(() => {
    if (!isOpen) return; // Only run when modal opens
    if (event) {
      // Reset form only if event really changed (by id)
      if (prevEventRef.current && prevEventRef.current.id === event.id) {
        console.log('[DEBUG FRONT] useEffect: same event, skip reset');
        return;
      }
      prevEventRef.current = event;
      console.log('[DEBUG FRONT] useEffect: initializing form for event', event.id, 'status:', event.status, 'objects length:', objects.length);
      const status = event.status
        ? event.status
        : event.approved === true ? 'approved' : 'not_approved';
      console.log('[DEBUG FRONT] useEffect event:', event, 'calculated status:', status);
      const objectIds = Array.isArray(event.objectIds) ? event.objectIds : [];
      const mainStart = event.startDate ? toDatetimeLocal(event.startDate) : (event.start_date ? toDatetimeLocal(event.start_date) : '');
      const mainEnd = event.endDate ? toDatetimeLocal(event.endDate) : (event.end_date ? toDatetimeLocal(event.end_date) : '');
      const objectDates = {};
      if (event.objectDates && typeof event.objectDates === 'object') {
        for (const oid of objectIds) {
          const d = event.objectDates[oid] || event.objectDates[String(oid)];
          const objStart = d && d.startDate ? toDatetimeLocal(d.startDate) : '';
          const objEnd = d && d.endDate ? toDatetimeLocal(d.endDate) : '';
          // Храним только даты, явно отличающиеся от основных — иначе при смене только основных дат
          // в API уходили бы старые даты по объекту и event_objects не обновлялись бы
          objectDates[oid] = {
            startDate: objStart && objStart !== mainStart ? objStart : '',
            endDate: objEnd && objEnd !== mainEnd ? objEnd : ''
          };
        }
      }
      const orgId = event.organizationId ?? event.organization_id ?? '';
      const sportId = event.sportTypeId ?? event.sport_type_id ?? '';
      setFormData({
        name: event.name || '',
        startDate: mainStart,
        endDate: mainEnd,
        description: event.description || '',
        cloudUrl: event.cloud_url ?? event.cloudUrl ?? '',
        status,
        objectIds,
        objectDates,
        organizationId: orgId !== null && orgId !== undefined ? String(orgId) : '',
        sportTypeId: sportId !== null && sportId !== undefined ? String(sportId) : ''
      });
     } else if (mode === 'create' && defaultDate) {
       const start = new Date(defaultDate);
       const currentObjects = objectsRef.current;
       const objectIds = defaultObjectId ? [defaultObjectId] : (currentObjects.length ? [currentObjects[0].id] : []);
      const defaultOrgId = userOrganizationId ? String(userOrganizationId) : '';
      setFormData({
        name: '',
        startDate: toDatetimeLocal(start.toISOString()),
        endDate: '',
        description: '',
        cloudUrl: '',
        status: 'not_approved',
        objectIds,
        objectDates: {},
        organizationId: defaultOrgId,
        sportTypeId: ''
      });
     } else {
       const defaultOrgId = userOrganizationId ? String(userOrganizationId) : '';
       const currentObjects = objectsRef.current;
       setFormData({
         name: '',
         startDate: '',
         endDate: '',
         description: '',
         cloudUrl: '',
         status: 'not_approved',
         objectIds: defaultObjectId ? [defaultObjectId] : (currentObjects.length ? [currentObjects[0].id] : []),
         objectDates: {},
         organizationId: defaultOrgId,
         sportTypeId: ''
       });
     }
   }, [event?.id, isOpen, mode, defaultDate, defaultObjectId]);

  // Дебаг изменения статуса — показывает, кто и когда меняет статус
  useEffect(() => {
    console.log('[DEBUG FRONT] formData.status changed to:', formData.status, 'stack:', new Error().stack);
  }, [formData.status]);

  // Загрузка организаций при открытии модалки
  useEffect(() => {
    if (isOpen) {
      referencesAPI.getOrganizations().then(setOrganizations).catch(() => setOrganizations([]));
    }
  }, [isOpen]);

  // Загрузка видов спорта при выборе организатора
  useEffect(() => {
    if (!formData.organizationId) {
      setSportTypes([]);
      return;
    }
    referencesAPI.getSportTypes(parseInt(formData.organizationId, 10)).then(setSportTypes).catch(() => setSportTypes([]));
  }, [formData.organizationId]);

  const canEditSportType = userRole === 'superuser' ||
    userRole === 'manager' ||
    (userOrganizationId &&
    formData.organizationId &&
    String(userOrganizationId) === String(formData.organizationId)
  );

  const handleChange = (e) => {
    const { name, value } = e.target;
    console.log('[DEBUG FRONT] handleChange:', name, value);
    if (name === 'organizationId') {
      setFormData(prev => {
        console.log('[DEBUG FRONT] setFormData from handleChange (org change), prev status:', prev.status);
        return { ...prev, [name]: value, sportTypeId: '' };
      });
    } else {
      setFormData(prev => {
        console.log('[DEBUG FRONT] setFormData from handleChange, prev status:', prev.status, 'new status:', value);
        return { ...prev, [name]: value };
      });
    }
  };

  const addObjectId = (objectId) => {
    if (!objectId || formData.objectIds.includes(objectId)) return;
    const objectDates = { ...formData.objectDates };
    objectDates[objectId] = { startDate: formData.startDate, endDate: formData.endDate };
    setFormData({ ...formData, objectIds: [...formData.objectIds, objectId], objectDates });
  };

  const removeObjectId = (objectId) => {
    if (!canEditObjects) return;
    const ids = formData.objectIds.filter(id => id !== objectId);
    const objectDates = { ...formData.objectDates };
    delete objectDates[objectId];
    setFormData({ ...formData, objectIds: ids.length ? ids : formData.objectIds, objectDates });
  };

  const setObjectDate = (objectId, field, value) => {
    const objectDates = { ...formData.objectDates };
    if (!objectDates[objectId]) objectDates[objectId] = { startDate: '', endDate: '' };
    objectDates[objectId][field] = value;
    setFormData({ ...formData, objectDates });
  };

  const getObjectStartEnd = (objectId) => {
    const d = formData.objectDates[objectId];
    return {
      start: (d && d.startDate) ? d.startDate : formData.startDate,
      end: (d && d.endDate) ? d.endDate : formData.endDate
    };
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setValidationError('');
    
    // Валидация дат
    if (new Date(formData.endDate) <= new Date(formData.startDate)) {
      setValidationError('Дата окончания должна быть позже даты начала');
      return;
    }
    if (!formData.objectIds || formData.objectIds.length === 0) {
      setValidationError('Выберите хотя бы один объект');
      return;
    }
    // Валидация дат по объектам
    const objectDates = {};
    for (const oid of formData.objectIds) {
      const { start, end } = getObjectStartEnd(oid);
      if (start && end && new Date(end) <= new Date(start)) {
        const obj = objects.find(o => o.id === oid);
        setValidationError(`На объекте «${obj ? obj.name : oid}» дата окончания должна быть позже даты начала`);
        return;
      }
      objectDates[oid] = { startDate: start || '', endDate: end || '' };
    }
    console.log('[DEBUG FRONT] Submitting event data:', { ...formData, objectDates });
    onSave({ ...formData, objectDates });
    onClose();
  };

  const handleDelete = () => {
    // Подтверждение удаления выполняется на уровне родительского компонента (SportsScheduleTable).
    // Здесь просто вызываем обработчик.
    if (onDelete) {
      onDelete();
    }
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="modal-overlay">
      <div
        className="modal-content"
        role="dialog"
        aria-modal="true"
        aria-labelledby="event-modal-title"
        tabIndex={-1}
        ref={dialogRef}
      >
        <h2 id="event-modal-title">{mode === 'create' ? 'Создать мероприятие' : 'Редактировать мероприятие'}</h2>
        {validationError && (
          <div className="form-error" role="alert">{validationError}</div>
        )}
        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label htmlFor="event-name">Название мероприятия:</label>
            <input
              id="event-name"
              type="text"
              name="name"
              value={formData.name}
              onChange={handleChange}
              required
            />
          </div>

          <div className="date-fields-container">
            <div className="form-group">
              <label htmlFor="event-start">Дата и время начала:</label>
              <input
                id="event-start"
                type="datetime-local"
                name="startDate"
                value={formData.startDate}
                onChange={handleChange}
                required
              />
            </div>

            <div className="form-group">
              <label htmlFor="event-end">Дата и время окончания:</label>
              <input
                id="event-end"
                type="datetime-local"
                name="endDate"
                value={formData.endDate}
                onChange={handleChange}
                required
              />
            </div>
          </div>

          <div className="form-group">
            <label htmlFor="event-description">Описание:</label>
            <textarea
              id="event-description"
              name="description"
              value={formData.description}
              onChange={handleChange}
            />
          </div>

          <div className="form-group">
            <label htmlFor="event-cloud-url">Ссылка на документы (облако):</label>
            <input
              id="event-cloud-url"
              type="url"
              name="cloudUrl"
              value={formData.cloudUrl}
              onChange={handleChange}
              placeholder="https://..."
            />
          </div>

          <div className="form-group">
            <label htmlFor="event-organization">Организатор:</label>
            {userRole === 'superuser' || userRole === 'manager' ? (
              <select
                id="event-organization"
                name="organizationId"
                value={formData.organizationId}
                onChange={handleChange}
              >
                <option value="">— не выбрано —</option>
                {organizations.map((org) => (
                  <option key={org.id} value={org.id}>{org.name}</option>
                ))}
              </select>
            ) : (
              <div className="organization-readonly">
                {organizations.find(o => o.id === parseInt(formData.organizationId, 10))?.name || '— не выбрано —'}
              </div>
            )}
          </div>

          <div className="form-group">
            <label htmlFor="event-sport-type">Вид спорта:</label>
            {canEditSportType ? (
              <select
                id="event-sport-type"
                name="sportTypeId"
                value={formData.sportTypeId}
                onChange={handleChange}
                disabled={!formData.organizationId}
              >
                <option value="">— не выбрано —</option>
                {sportTypes.map((st) => (
                  <option key={st.id} value={st.id}>{st.name}</option>
                ))}
              </select>
            ) : (
              <div className="sport-type-readonly">
                {formData.sportTypeId
                  ? sportTypes.find(st => String(st.id) === String(formData.sportTypeId))?.name || `ID: ${formData.sportTypeId}`
                  : '— не выбрано —'}
              </div>
            )}
          </div>

          <div className="form-group">
            <label htmlFor="event-status">Статус:</label>
            {canEditStatus ? (
              <select
                id="event-status"
                name="status"
                value={formData.status}
                onChange={handleChange}
              >
                <option value="not_approved">Не утверждено</option>
                <option value="approved">Утверждено</option>
                <option value="cancelled">Отменено</option>
              </select>
            ) : (
              <div>
                {formData.status === 'approved' ? 'Утверждено' : 
                 formData.status === 'cancelled' ? 'Отменено' : 'Не утверждено'}
              </div>
            )}
          </div>

          {objects.length > 0 && (
            <fieldset className="form-group event-objects-fieldset">
              <legend>Объекты (мероприятие отображается на выбранных){!canEditObjects && ' (только просмотр)'}</legend>
              <div className="event-objects-added">
                {formData.objectIds.map(id => {
                  const obj = objects.find(o => o.id === id);
                  if (!obj) return null;
                  const { start, end } = getObjectStartEnd(id);
                  return (
                    <div key={obj.id} className="event-object-block">
                      <div className="event-object-tag">
                        <span>{obj.name}</span>
                        {canEditObjects && (
                          <button type="button" className="event-object-remove" onClick={() => removeObjectId(obj.id)} title="Удалить" aria-label={`Удалить объект «${obj.name}»`}>×</button>
                        )}
                      </div>
                      <div className="event-object-dates">
                        <label className="event-object-date-label" htmlFor={`event-obj-start-${id}`}>Начало:</label>
                        <input
                          id={`event-obj-start-${id}`}
                          type="datetime-local"
                          className="event-object-datetime"
                          value={start}
                          onChange={(e) => setObjectDate(id, 'startDate', e.target.value)}
                          disabled={!canEditObjects}
                        />
                        <label className="event-object-date-label" htmlFor={`event-obj-end-${id}`}>Окончание:</label>
                        <input
                          id={`event-obj-end-${id}`}
                          type="datetime-local"
                          className="event-object-datetime"
                          value={end}
                          onChange={(e) => setObjectDate(id, 'endDate', e.target.value)}
                          disabled={!canEditObjects}
                        />
                      </div>
                    </div>
                  );
                })}
              </div>
              {canEditObjects && (
                <div className="event-objects-add-row">
                  <label htmlFor="event-add-object" className="sr-only">Добавить объект</label>
                  <select
                    id="event-add-object"
                    ref={addObjectSelectRef}
                    className="event-object-select"
                    value=""
                    onChange={(e) => {
                      const id = e.target.value ? parseInt(e.target.value, 10) : null;
                      if (id) addObjectId(id);
                      e.target.value = '';
                    }}
                  >
                    <option value="">— добавить объект —</option>
                    {objects
                      .filter(o => !formData.objectIds.includes(o.id))
                      .map(o => (
                        <option key={o.id} value={o.id}>{o.name}</option>
                      ))}
                  </select>
                  <button type="button" className="event-object-add-btn" onClick={() => {
                    const select = addObjectSelectRef.current;
                    if (select && select.value) {
                      addObjectId(parseInt(select.value, 10));
                      select.value = '';
                    }
                  }} title="Добавить объект" aria-label="Добавить объект">+</button>
                </div>
              )}
            </fieldset>
          )}

          <div className="modal-buttons">
            <button type="submit" className="save-button">Сохранить</button>
            {mode === 'edit' && onDelete && (
              <button type="button" className="delete-button" onClick={handleDelete}>Удалить</button>
            )}
            <button type="button" className="cancel-button" onClick={onClose}>Отмена</button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default EventModal; 